import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiService } from '../../../services/ApiService';
import type { StrategyNode } from '../../../types/BaseNode';
import { serializeNode } from '../../../utils/serializeNode';
import { useDomainContext } from '../../../contexts/DomainContext';
import {
    MONITOR_TEXT,
    MonitorCoreFields,
    MonitorRelevanceRuleFields,
    monitorSecondaryButtonStyle,
    parseLookbackDays
} from './MonitorFormParts';

interface Props {
    node: StrategyNode;     // The subtree to monitor
    rootNode: StrategyNode; // The full tree for context
    start: string | null;
    end: string | null;
    position?: { x: number, y: number } | null;
    onClose: () => void;
    onSuccess: () => void;
    monitorId?: number;
}

export function SaveToMonitorModal({ node, rootNode, start, end, position, onClose, onSuccess, monitorId }: Props) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [action, setAction] = useState('');
    const [lookbackDays, setLookbackDays] = useState<string>('1');
    const [loading, setLoading] = useState(false);

    const { activeDomainId } = useDomainContext();

    // Relevance Rule fields
    const [relevanceMetric, setRelevanceMetric] = useState<string>('');
    const [relevanceOperator, setRelevanceOperator] = useState<string>('>');
    const [relevanceThreshold, setRelevanceThreshold] = useState<string>('');

    const handleSave = async () => {
        setLoading(true);
        try {
            const conditionJson = serializeNode(node, start, end);
            const strategyJson = serializeNode(rootNode, start, end);

            if (monitorId) {
                await ApiService.updateAlertLogic(String(monitorId), {
                    strategy_json: strategyJson,
                    condition_json: conditionJson
                });
            } else {
                const numLookback = parseLookbackDays(lookbackDays);
                if (numLookback === null) {
                    alert(MONITOR_TEXT.invalidLookback);
                    return;
                }

                await ApiService.createAlert({
                    name,
                    description,
                    action,
                    condition_json: conditionJson,
                    strategy_json: strategyJson,
                    lookback_days: numLookback,
                    enabled: 1,
                    relevance_metric: relevanceMetric || undefined,
                    relevance_operator: relevanceMetric ? relevanceOperator : undefined,
                    relevance_threshold: relevanceMetric ? parseFloat(relevanceThreshold) : undefined
                }, activeDomainId || undefined);
            }
            onSuccess();
            onClose();
        } catch (e) {
            console.error('Failed to save strategy', e);
            const errorMessage = e instanceof Error ? e.message : MONITOR_TEXT.genericSaveError;
            alert(`Error: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    // Calculate positioning
    const modalStyle: React.CSSProperties = {
        backgroundColor: 'var(--bg-panel)',
        padding: '2rem',
        borderRadius: '8px',
        width: '450px',
        boxShadow: 'var(--shadow-modal-sm)',
        position: 'relative',
        color: 'var(--text-primary)'
    };

    if (position) {
        // Adjust position so it doesn't go off screen
        const modalWidth = 450;
        const modalHeight = 550; // Estimated height
        const margin = 20;

        let left = position.x;
        let top = position.y;

        if (left + modalWidth + margin > window.innerWidth) {
            left = window.innerWidth - modalWidth - margin;
        }
        if (top + modalHeight + margin > window.innerHeight) {
            top = window.innerHeight - modalHeight - margin;
        }

        modalStyle.position = 'fixed';
        modalStyle.left = `${left}px`;
        modalStyle.top = `${top}px`;
    }

    return createPortal(
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'var(--overlay-backdrop-soft)', // Lighter backdrop for positional modals
                display: position ? 'block' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}
        >
            <div
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={modalStyle}
            >
                {monitorId ? (
                    <>
                        <h3 style={{ marginTop: 0 }}>🛡️ Update Condition for Monitor #{monitorId}</h3>
                        <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                            This will update the active condition logic for the current monitor, using this specific node as the alert trigger.
                        </p>
                    </>
                ) : (
                    <>
                        <h3 style={{ marginTop: 0 }}>🛡️ Save for Monitoring</h3>
                        <MonitorCoreFields
                            name={name}
                            description={description}
                            action={action}
                            lookbackDays={lookbackDays}
                            onNameChange={setName}
                            onDescriptionChange={setDescription}
                            onActionChange={setAction}
                            onLookbackDaysChange={setLookbackDays}
                        />
                        <MonitorRelevanceRuleFields
                            relevanceMetric={relevanceMetric}
                            relevanceOperator={relevanceOperator}
                            relevanceThreshold={relevanceThreshold}
                            onRelevanceMetricChange={setRelevanceMetric}
                            onRelevanceOperatorChange={setRelevanceOperator}
                            onRelevanceThresholdChange={setRelevanceThreshold}
                        />
                    </>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button onClick={onClose} disabled={loading} style={monitorSecondaryButtonStyle}>Cancel</button>
                    <button
                        onClick={handleSave}
                        disabled={loading || (!monitorId && (!name.trim() || !description.trim() || !action.trim()))}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: 'var(--accent-primary)',
                            color: 'white',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: (loading || (!monitorId && (!name.trim() || !description.trim() || !action.trim()))) ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold'
                        }}
                    >
                        {loading ? 'Saving...' : (monitorId ? '🔄 Update Condition' : '🚀 Start Monitoring')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}


